import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC, ReactNode } from 'react';
import { ModalBodyWrapper } from 'src/components/form/form/ModalBodyWrapper';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import ObligationAssessmentResultForm from 'src/pages/assessments/forms/ObligationAssessmentResultForm';
import type { ObligationAssessmentResultFormDataFields } from 'src/pages/assessments/forms/obligationAssessmentResultSchema';
import { defaultValues } from 'src/pages/assessments/forms/obligationAssessmentResultSchema';
import { isResultFormReadOnly } from 'src/pages/assessments/forms/resultFormReadOnly';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useMultiParentFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useInsertObligationOversightResult } from '@/hooks/mutations/oversight-result/useInsertObligationOversightResult';
import { useUpdateObligationOversightResult } from '@/hooks/mutations/oversight-result/useUpdateObligationOversightResult';
import { useGetObligationOversightResultById } from '@/hooks/queries';
import { saveFilesForOptionalCreatedEntity } from '@/utils/createWithFiles';
import { oversightAssessmentResultsUrl } from '@/utils/urls';

type Props = {
  readonly: boolean;
  navigateToResults: boolean;
  isModalForm: boolean;
  parentId?: string;
  assessedItem?: ObjectWithContributors;
  id?: string;
  onDismiss?: (saved: boolean) => void;
  beforeFieldsSlot?: ReactNode;
  showAssessmentSelector?: boolean;
  obligationIds?: string[];
  header?: string;
};

const ConnectedObligationOversightResultForm: FC<Props> = ({
  readonly,
  parentId,
  assessedItem,
  id,
  onDismiss,
  beforeFieldsSlot,
  showAssessmentSelector,
  navigateToResults,
  isModalForm,
  obligationIds,
  header,
}) => {
  const { data } = useGetObligationOversightResultById({
    queryArgs: { id: id! },
    shouldSkip: !id,
  });
  const obligationAssessmentResult = data?.obligation_oversight_result?.[0];
  const {
    hasPermission: canUpdateObligationAssessmentResult,
    loading: isLoadingCanUpdateObligationAssessmentResult,
  } = useHasPermissionQuery('update:obligation_oversight_result', assessedItem);
  const { updateFiles } = useMultiParentFileUpdate();
  const { insertObligationOversightResult } =
    useInsertObligationOversightResult();
  const { updateObligationOversightResult: updateObligationAssessmentResult } =
    useUpdateObligationOversightResult();

  obligationIds = obligationIds ?? [];
  const parentObligation = obligationAssessmentResult?.parents.find(
    (p) => p.obligation
  );
  if (parentObligation?.obligation?.Id) {
    obligationIds.push(parentObligation.obligation.Id);
  } else if (assessedItem?.Id) {
    obligationIds.push(assessedItem.Id);
  }

  const onSave = async (values: ObligationAssessmentResultFormDataFields) => {
    const { files } = values;
    const oversightAssessmentId =
      parentId || values.OversightAssessmentId || undefined;
    const obligationAssessmentResultIds: string[] = [];
    if (!obligationAssessmentResult && !oversightAssessmentId) {
      throw new Error('An oversight assessment is required');
    }
    if (obligationAssessmentResult) {
      const result = await updateObligationAssessmentResult({
        Rating: values.Rating,
        Rationale: values.Rationale,
        TestDate: values.TestDate,
        CustomAttributeData: values.CustomAttributeData ?? null,
        Id: obligationAssessmentResult.Id,
      });

      if (result.update_obligation_oversight_result?.affected_rows === 0) {
        throw new Error('Obligation second line result update failed');
      }
      obligationAssessmentResultIds.push(id!);
    } else {
      const result = await insertObligationOversightResult({
        OversightAssessmentId: oversightAssessmentId!,
        ObligationIds: values?.ObligationIds?.map((r) => r.value) ?? [],
        Rating: values.Rating,
        Rationale: values.Rationale,
        TestDate: values.TestDate,
        CustomAttributeData: values.CustomAttributeData ?? null,
      });

      if (!result.insertChildObligationOversightResult?.Ids?.[0]) {
        throw new Error('Obligation second line result id is missing');
      }
      obligationAssessmentResultIds.push(
        ...result.insertChildObligationOversightResult.Ids
      );
    }

    const createdEntity = !obligationAssessmentResult
      ? {
          id: obligationAssessmentResultIds[0],
          destination: oversightAssessmentResultsUrl(oversightAssessmentId!),
        }
      : undefined;
    const saveFiles = () =>
      updateFiles({
        parentIds: obligationAssessmentResultIds,
        parentType: Parent_Type_Enum.ObligationOversightResult,
        selectedFiles: files,
        originalFiles: obligationAssessmentResult?.files.map((f) => f.file),
      });
    await saveFilesForOptionalCreatedEntity({ createdEntity, saveFiles });

    return {
      savedIds: obligationAssessmentResultIds,
      createdEntity,
      successNavigation:
        navigateToResults && oversightAssessmentId
          ? {
              destination: oversightAssessmentResultsUrl(oversightAssessmentId),
            }
          : undefined,
    };
  };

  return (
    <ObligationAssessmentResultForm
      header={header}
      onDismiss={onDismiss}
      defaultValues={{
        ...defaultValues,
        ObligationIds: obligationIds.map((c) => ({ value: c })),
        OversightAssessmentId: parentId ?? null,
      }}
      values={
        obligationAssessmentResult
          ? {
              Rating: obligationAssessmentResult?.Rating ?? 1,
              Rationale: obligationAssessmentResult?.Rationale ?? '',
              OversightAssessmentId: parentId,
              ObligationIds: obligationIds.map((c) => ({ value: c })),
              TestDate: obligationAssessmentResult?.TestDate,
              CustomAttributeData:
                obligationAssessmentResult?.CustomAttributeData,
              files: obligationAssessmentResult?.files.map((f) => f.file),
            }
          : undefined
      }
      onSave={onSave}
      readOnly={isResultFormReadOnly({
        readonly,
        permissionLoading: isLoadingCanUpdateObligationAssessmentResult,
        canUpdate: canUpdateObligationAssessmentResult,
      })}
      renderTemplate={(renderProps) =>
        isModalForm ? (
          <ModalBodyWrapper {...renderProps} />
        ) : (
          <PageWrapper {...renderProps} />
        )
      }
      disableObligationSelector={id != undefined}
      beforeFieldsSlot={beforeFieldsSlot}
      showSelector={showAssessmentSelector ? 'oversight_assessment' : undefined}
      assessmentMode={'oversight_assessment'}
    />
  );
};

export default ConnectedObligationOversightResultForm;
